import React, { useCallback, useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import './Calendario.css';

function eventColorClass(event) {
  const seed = `${event?.id || ''}-${event?.title || ''}`;
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) hash = (hash * 31 + seed.charCodeAt(index)) | 0;
  return `calendar-event--palette-${Math.abs(hash) % 6}`;
}

function renderCalendarEvent(info) {
  const title = String(info.event.title || '').toUpperCase();
  return (
    <div className="calendar-event-content">
      <span className="calendar-event-dot" aria-hidden="true" />
      {info.timeText && <span className="calendar-event-time">{info.timeText}</span>}
      <span className="calendar-event-name">{title}</span>
      {!info.event.allDay && <span className="calendar-event-clock" aria-hidden="true">◷</span>}
    </div>
  );
}

const EMPTY = { titulo: '', fecha: '', hora: '', descripcion: '', recordatorio: true };

function localDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function localTime(event) {
  if (event?.extendedProps?.hora) return event.extendedProps.hora;
  if (event?.allDay) return '';
  const date = new Date(event?.start);
  return Number.isNaN(date.getTime()) ? '' : date.toTimeString().slice(0, 5);
}

const CalendarioPersonal = () => {
  const { showSuccess, showError } = useToast();
  const calendarRef = useRef(null);
  const rangeRef = useRef('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const loadEvents = useCallback(async (range, force = false) => {
    const key = range ? `${range.startStr}|${range.endStr}` : 'current';
    if (!force && rangeRef.current === key) return;
    rangeRef.current = key;
    try {
      const response = await api.get('/calendario-personal', { params: range ? { start: range.startStr, end: range.endStr } : {} });
      setEvents(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      rangeRef.current = '';
      setError('No se pudo cargar la agenda');
      console.error('Error cargando agenda personal:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) loadEvents({ startStr: calendar.view.activeStart.toISOString(), endStr: calendar.view.activeEnd.toISOString() });
  }, [loadEvents]);

  const openNew = () => {
    const now = new Date();
    const today = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    setSelected(null);
    setForm({ ...EMPTY, fecha: today });
    setModal(true);
  };

  const openEdit = (info) => {
    setSelected(info.event);
    setForm({ titulo: info.event.title || '', fecha: localDate(info.event.start), hora: localTime(info.event), descripcion: info.event.extendedProps?.descripcion || '', recordatorio: info.event.extendedProps?.recordatorio !== false });
    setModal(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.titulo.trim() || !form.fecha) return;
    setSaving(true);
    const payload = { titulo: form.titulo.trim(), fecha: form.fecha, hora: form.hora || null, descripcion: form.descripcion.trim() || null, recordatorio: Boolean(form.recordatorio) };
    try {
      if (selected) {
        await api.patch(`/calendario-personal/${selected.extendedProps.eventoId}`, payload);
        showSuccess('Evento actualizado');
      } else {
        await api.post('/calendario-personal', payload);
        showSuccess('Evento guardado');
      }
      setModal(false);
      await loadEvents(null, true);
    } catch (err) {
      showError(err?.response?.data?.error || 'No se pudo guardar el evento');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected || !window.confirm('¿Eliminar este evento?')) return;
    setSaving(true);
    try {
      await api.delete(`/calendario-personal/${selected.extendedProps.eventoId}`);
      setModal(false);
      showSuccess('Evento eliminado');
      await loadEvents(null, true);
    } catch (err) {
      showError(err?.response?.data?.error || 'No se pudo eliminar el evento');
    } finally {
      setSaving(false);
    }
  };

  const move = async (info) => {
    try {
      await api.patch(`/calendario-personal/${info.event.extendedProps.eventoId}`, { fecha: localDate(info.event.start), hora: localTime(info.event) || null });
    } catch (err) {
      info.revert();
      showError(err?.response?.data?.error || 'No se pudo mover el evento');
    }
  };

  return (
    <div className="calendario-container calendario-personal">
      <div className="calendario-header"><h1>Mi agenda personal</h1><button className="btn-primary" type="button" onClick={openNew}>+ Nuevo evento</button></div>
      {error && <div className="calendario-error">{error}</div>}
      <div className="calendario-main">
        {loading && <div className="calendario-loading">Cargando...</div>}
        <FullCalendar ref={calendarRef} plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} initialView="dayGridMonth" hiddenDays={[0]} locale="es" height="auto" headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }} buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }} events={events} eventClassNames={(info) => eventColorClass(info.event)} eventContent={renderCalendarEvent} editable eventClick={openEdit} eventDrop={move} eventResize={move} datesSet={(range) => loadEvents(range)} noEventsContent="No hay eventos" />
      </div>
      {modal && <div className="calendario-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setModal(false); }}>
        <form className="calendario-modal" onSubmit={save}>
          <div className="calendario-modal-header"><h2>{selected ? 'Editar evento' : 'Nuevo evento'}</h2><button type="button" className="calendario-modal-close" onClick={() => !saving && setModal(false)}>×</button></div>
          <label>Título<input value={form.titulo} onChange={(event) => setForm({ ...form, titulo: event.target.value })} required maxLength={255} autoFocus /></label>
          <div className="calendario-form-grid"><label>Fecha<input type="date" value={form.fecha} onChange={(event) => setForm({ ...form, fecha: event.target.value })} required /></label><label>Hora<input type="time" value={form.hora} onChange={(event) => setForm({ ...form, hora: event.target.value })} /></label></div>
          <label>Descripción<textarea value={form.descripcion} onChange={(event) => setForm({ ...form, descripcion: event.target.value })} rows="3" /></label>
          <label className="calendario-check"><input type="checkbox" checked={form.recordatorio} onChange={(event) => setForm({ ...form, recordatorio: event.target.checked })} /> Recordatorio</label>
          <div className="calendario-modal-actions">{selected && <button type="button" className="btn-danger" onClick={remove} disabled={saving}>Eliminar</button>}<span /><button type="button" className="btn-secondary" onClick={() => setModal(false)} disabled={saving}>Cancelar</button><button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button></div>
        </form>
      </div>}
    </div>
  );
};

export default CalendarioPersonal;
